import React from 'react';
import AgentCard from '../AgentCard';
import ListingCreateModal from '../ListingCreateModal';
import ListingEditModal from '../ListingEditModal';
import PropertyEditModal from '../PropertyEditModal';
import ReservationRequestsPanel from '../ReservationRequestsPanel';
import TabNavigation from '../TabNavigation';
import { PROPERTY_LIFECYCLE_LABELS, PROPERTY_WIZARD_STEPS } from '../propertyWizardConfig';

function StatusBadge({ status = 'DRAFT' }) {
  return (
    <span className={`status-badge status-${status.toLowerCase()}`}>
      {PROPERTY_LIFECYCLE_LABELS[status] || status}
    </span>
  );
}

function PropertyCard({ property, listings, onEdit, onCreateListing, onDelete }) {
  const completedStep = property.completed_step || 0;
  const finalStep = PROPERTY_WIZARD_STEPS.length - 1;
  const progress = Math.min(100, Math.round((completedStep / finalStep) * 100));

  return (
    <article className="property-card rich-card property-card--asset">
      <div className="rich-card-header">
        <span className="rich-card-title">{property.name || `ملک ${property.id}`}</span>
        <StatusBadge status={property.lifecycle_status} />
      </div>
      <div className="rich-card-body">
        {property.address && <p className="property-card-address" title={property.address}>{property.address}</p>}
        <div className="property-card-facts">
          {property.area && <div className="property-card-fact"><span>مساحت</span><strong>{property.area} م²</strong></div>}
          {property.property_type && <div className="property-card-fact"><span>نوع ملک</span><strong>{property.property_type}</strong></div>}
        </div>
        <div className="card-progress">
          <div className="card-progress-label"><span>پیشرفت ثبت</span><span>{Math.min(completedStep, finalStep)} / {finalStep} مرحله</span></div>
          <div className="card-progress-bar"><div className="card-progress-fill" style={{ width: `${progress}%` }} /></div>
        </div>
        {listings.length > 0 && (
          <div className="card-listings">
            {listings.map(listing => (
              <span key={listing.id} className={`listing-chip ${listing.listing_type === 'SALE' ? 'chip-sale' : 'chip-rent'}`}>
                {listing.listing_type === 'SALE' ? 'فروش' : 'اجاره'}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="property-actions">
        <button type="button" className="continue-button" onClick={() => onEdit(property)}>ویرایش ملک</button>
        <button type="button" className="create-listing-button" onClick={() => onCreateListing(property)}>ایجاد آگهی</button>
        <button type="button" className="delete-button" onClick={() => onDelete(property.id, property.name)}>حذف</button>
      </div>
    </article>
  );
}

function ListingCard({ listing, type, onEdit, onDelete }) {
  const isSale = type === 'SALE';
  const propertyTitle = listing.property?.name || listing.property?.address || `ملک ${listing.property?.id ?? listing.property}`;

  return (
    <article className="property-card rich-card">
      <div className="rich-card-header">
        <span className="rich-card-title">آگهی {isSale ? 'فروش' : 'اجاره'} #{listing.id}</span>
        <StatusBadge status={listing.lifecycle_status} />
      </div>
      <div className="rich-card-body">
        <div className="card-row"><span>ملک: {propertyTitle}</span></div>
        {isSale ? (
          <>
            <div className="card-row"><span>قیمت: {listing.sale_details?.asking_price ? `${Number(listing.sale_details.asking_price).toLocaleString('fa-IR')} تومان` : 'تعریف نشده'}</span></div>
            {listing.sale_details?.mortgage_status && <div className="card-row"><span>وام: {listing.sale_details.mortgage_status === 'AVAILABLE' ? 'دارد' : 'ندارد'}</span></div>}
            {listing.sale_details?.has_tenant !== undefined && <div className="card-row"><span>وضعیت سکونت: {listing.sale_details.has_tenant ? 'دارای مستاجر' : 'خالی'}</span></div>}
          </>
        ) : (
          <>
            <div className="card-row"><span>اجاره: {listing.rent_details?.ideal_rent ? `${Number(listing.rent_details.ideal_rent).toLocaleString('fa-IR')} تومان` : 'تعریف نشده'}</span></div>
            <div className="card-row"><span>ودیعه: {listing.rent_details?.ideal_deposit ? `${Number(listing.rent_details.ideal_deposit).toLocaleString('fa-IR')} تومان` : 'تعریف نشده'}</span></div>
            {listing.rent_details?.furnished_status && <div className="card-row"><span>وضعیت مبلمان: {{ UNFURNISHED: 'بدون مبلمان', SEMI_FURNISHED: 'نیمه مبله', FULLY_FURNISHED: 'کاملاً مبله' }[listing.rent_details.furnished_status] || listing.rent_details.furnished_status}</span></div>}
          </>
        )}
        <div className="card-row"><span>درخواست‌های رزرو: {listing.reservation_count ?? 0}</span></div>
      </div>
      <div className="property-actions">
        <button type="button" className="continue-button" onClick={() => onEdit(listing)}>ویرایش آگهی</button>
        <button type="button" className="delete-button" onClick={() => onDelete(listing.id, type)}>حذف</button>
      </div>
      <ReservationRequestsPanel listingId={listing.id} token={localStorage.getItem('access_token')} />
    </article>
  );
}

function EmptyState({ children }) {
  return <p className="empty-state">{children}</p>;
}

function PropertyDashboard({
  activeTab,
  counts,
  agentRelations,
  properties,
  listings,
  editingListing,
  editingProperty,
  creatingListingForProperty,
  onTabChange,
  onAddAgent,
  onAssignProperty,
  onRateAgent,
  onRemoveAgent,
  onEditListing,
  onEditProperty,
  onCreateListing,
  onDeleteListing,
  onDeleteProperty,
  onNewProperty,
  onRefresh,
}) {
  const saleListings = listings.filter(listing => listing.listing_type === 'SALE');
  const rentListings = listings.filter(listing => listing.listing_type === 'RENT');

  return (
    <div className="dashboard-container">
      <h2>مدیریت املاک و آگهی‌ها</h2>
      <TabNavigation activeTab={activeTab} onTabChange={onTabChange} counts={counts} />
      <div className="tab-content">
        {activeTab === 'agents' && (
          <div className="agents-tab">
            {agentRelations.length === 0 ? <EmptyState>شما هیچ نماینده‌ای ندارید.</EmptyState> : (
              <div className="agent-grid">
                {agentRelations.map(relation => <AgentCard key={relation.id} relation={relation} onAssignProperty={onAssignProperty} onRate={onRateAgent} onRemove={onRemoveAgent} />)}
              </div>
            )}
            <button type="button" className="add-agent-button" onClick={onAddAgent}>افزودن نماینده جدید</button>
          </div>
        )}
        {activeTab === 'properties' && (
          <div className="draft-section properties-grid-section">
            {properties.length === 0 ? <EmptyState>شما هیچ ملکی ثبت نکرده‌اید.</EmptyState> : (
              <div className="property-grid property-assets-grid">
                {properties.map(property => <PropertyCard key={property.id} property={property} listings={listings.filter(listing => (listing.property?.id ?? listing.property) === property.id)} onEdit={onEditProperty} onCreateListing={onCreateListing} onDelete={onDeleteProperty} />)}
              </div>
            )}
          </div>
        )}
        {activeTab === 'sale' && (
          <div className="draft-section">
            {saleListings.length === 0 ? <EmptyState>شما هیچ آگهی فروشی ندارید.</EmptyState> : <div className="property-grid">{saleListings.map(listing => <ListingCard key={listing.id} listing={listing} type="SALE" onEdit={onEditListing} onDelete={onDeleteListing} />)}</div>}
          </div>
        )}
        {activeTab === 'rent' && (
          <div className="draft-section">
            {rentListings.length === 0 ? <EmptyState>شما هیچ آگهی اجاره‌ای ندارید.</EmptyState> : <div className="property-grid">{rentListings.map(listing => <ListingCard key={listing.id} listing={listing} type="RENT" onEdit={onEditListing} onDelete={onDeleteListing} />)}</div>}
          </div>
        )}
      </div>
      <div className="new-property-section"><button type="button" className="new-property-button" onClick={onNewProperty}>ثبت ملک جدید</button></div>
      {editingListing && <ListingEditModal listing={editingListing} onClose={() => onEditListing(null)} onSaved={onRefresh} />}
      {editingProperty && <PropertyEditModal property={editingProperty} onClose={() => onEditProperty(null)} onSaved={onRefresh} />}
      {creatingListingForProperty && <ListingCreateModal property={creatingListingForProperty} onClose={() => onCreateListing(null)} onSaved={onRefresh} />}
    </div>
  );
}

export default PropertyDashboard;
